import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import ReportsAPI from "../services/ReportsAPI";
import { getCookie } from "../utils/CookieUtil";
import { formatDateTime as formatDateTimeImport } from "../utils/DateTimeUtil";
import DateRangeField from "../../common/datetime/DateRangeField";
import {
  browserTimeZone,
  instantToWallClock,
  wallClockToIso,
} from "../../common/datetime/dateTimeParts";
import "./css/UserEmailReport.css";

/** The range is picked in the viewer's own zone. */
const TZ = browserTimeZone();

function UserEmailReport({ userUuid = null }) {
  const [emailReports, setEmailReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedUserUuid, setSelectedUserUuid] = useState("");

  // Set default date range (current month) and get current user
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0,
      23,
      59,
      59,
    );

    setStartDate(instantToWallClock(firstDayOfMonth, TZ));
    setEndDate(instantToWallClock(lastDayOfMonth, TZ));

    // Get current user ID from cookies if not provided as prop
    if (!userUuid) {
      const currentUserUuid = getCookie("user");
      if (currentUserUuid) {
        setSelectedUserUuid(currentUserUuid);
      }
    } else {
      setSelectedUserUuid(userUuid);
    }
  }, [userUuid]);

  // Auto-load reports when user ID and dates are available
  useEffect(() => {
    if (selectedUserUuid && startDate && endDate) {
      handleLoadReports(selectedUserUuid, startDate, endDate);
    }
  }, [selectedUserUuid, startDate, endDate]);

  const handleLoadReports = async (
    userUuidToLoad = selectedUserUuid,
    start = startDate,
    end = endDate,
  ) => {
    if (!userUuidToLoad || !start || !end) {
      setError("Please provide user ID, start date, and end date");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Convert datetime-local format to ISO string
      const startDateTime = wallClockToIso(start, TZ);
      const endDateTime = wallClockToIso(end, TZ);

      console.log(
        `Loading email reports for user ${userUuidToLoad} from ${startDateTime} to ${endDateTime}`,
      );

      const response = await ReportsAPI.getEmailReportsByUserUuidAndDateRange(
        userUuidToLoad,
        startDateTime,
        endDateTime,
      );

      if (response && response.success) {
        console.log("Email reports loaded successfully:", response.data);
        setEmailReports(response.data || []);
      } else {
        setError(response?.message || "Failed to load email reports");
        setEmailReports([]);
      }
    } catch (err) {
      console.error("Error loading email reports:", err);
      setError(err.message || "An error occurred while loading email reports");
      setEmailReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    handleLoadReports();
  };

  // Use centralized DateTimeUtil - imported at top of file
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return formatDateTimeImport(dateString);
  };
  const handleExportCSV = () => {
    if (emailReports.length === 0) {
      toast("No data to export", { icon: "⚠️" });
      return;
    } // Define CSV headers for specific email report columns
    const headers = [
      "ID",
      "Email Subject",
      "Delivered At",
      "Opened At",
      "Clicked Links",
      "Sophistication Level",
      "Training Category",
      "Open Count",
      "Total Clicks",
    ];

    // Convert data to CSV format
    const csvContent = [
      headers.join(","),
      ...emailReports.map((report) =>
        [
          report.id || "",
          `"${(report.emailSubject || report.subject || "").replace(
            /"/g,
            '""',
          )}"`,
          report.deliveredAt ? formatDate(report.deliveredAt) : "",
          report.openedAt ? formatDate(report.openedAt) : "",
          `"${(report.clickedLinks || "").replace(/"/g, '""')}"`,
          report.sophisticationLevel || "",
          report.trainingCategory || "",
          report.openCount || "0",
          report.totalClicks || "0",
        ].join(","),
      ),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `email-reports-user-${selectedUserUuid}-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="user-email-report-container">
      <div className="user-email-report-header">
        <h1 className="user-email-report-title">Email Reports</h1>
        <button
          className="user-email-report-export-button"
          onClick={handleExportCSV}
          disabled={emailReports.length === 0}
        >
          Export CSV
        </button>
      </div>

      <form onSubmit={handleFilterSubmit} className="user-email-report-filters">
        <DateRangeField
          legend="Report period"
          mode="datetime"
          fromLabel="From"
          toLabel="To"
          from={startDate}
          to={endDate}
          presets={["today", "7d", "30d", "mtd"]}
          onChange={({ from, to }) => {
            setStartDate(from);
            setEndDate(to);
          }}
        />

        <button
          type="submit"
          className="user-email-report-filter-button"
          disabled={loading}
        >
          {loading ? "Loading..." : "Load Reports"}
        </button>
      </form>

      {emailReports.length > 0 && (
        <div className="user-email-report-summary">
          <h3 className="user-email-report-summary-title">Summary</h3>
          <div className="user-email-report-date-range">
            Reports from {formatDate(startDate)} to {formatDate(endDate)}
          </div>
          <div className="user-email-report-summary-stats">
            <div className="user-email-report-stat">
              <div className="user-email-report-stat-value">
                {emailReports.length}
              </div>
              <div className="user-email-report-stat-label">Total Reports</div>
            </div>
            <div className="user-email-report-stat">
              <div className="user-email-report-stat-value">
                {selectedUserUuid}
              </div>
              <div className="user-email-report-stat-label">User ID</div>
            </div>
            <div className="user-email-report-stat">
              <div className="user-email-report-stat-value">
                {emailReports.filter((r) => r.status === "sent").length}
              </div>
              <div className="user-email-report-stat-label">Sent Emails</div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="user-email-report-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading && (
        <div className="user-email-report-loading">
          Loading email reports...
        </div>
      )}

      {!loading &&
        !error &&
        emailReports.length === 0 &&
        selectedUserUuid &&
        startDate &&
        endDate && (
          <div className="user-email-report-no-data">
            No email reports found for the specified criteria.
          </div>
        )}

      {!loading && !error && emailReports.length > 0 && (
        <div className="user-email-report-table-container">
          {" "}
          <table className="user-email-report-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email Subject</th>
                <th>Delivered At</th>
                <th>Opened At</th>
                <th>Clicked Links</th>
                <th>Sophistication Level</th>
                <th>Training Category</th>
                <th>Open Count</th>
                <th>Click Count</th>
              </tr>
            </thead>
            <tbody>
              {emailReports.map((report, index) => (
                <tr key={report.id || index}>
                  <td>{report.id || "N/A"}</td>
                  <td>{report.emailSubject || report.subject || "N/A"}</td>
                  <td>{formatDate(report.deliveredAt) || "N/A"}</td>
                  <td>{formatDate(report.openedAt) || "N/A"}</td>
                  <td>{report.clickedLinks || "N/A"}</td>
                  <td>{report.sophisticationLevel || "N/A"}</td>
                  <td>{report.trainingCategory || "N/A"}</td>
                  <td>{report.openCount || "0"}</td>
                  <td>{report.totalClicks || "0"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default UserEmailReport;
